import { useState, type FormEvent } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCredentialSave } from "@/lib/hooks/useCredentialSave";
import type { AffiliateCredentialPublic } from "@/lib/types";
import { CredentialHealth, CredentialShell, SaveFeedback } from "./CredentialShell";
import { Field } from "./Field";

const ERRORS: Record<string, string> = {
  invalid_rakuten_client_id: "Client ID inválido.",
  invalid_rakuten_site_id: "ID do site (sid) deve ter só números.",
  invalid_rakuten_secret: "Client Secret inválido (mínimo 8 caracteres, sem espaços).",
  rakuten_secret_required: "Informe o Client Secret.",
  tenant_not_found: "Conta não encontrada. Entre de novo no painel.",
};

interface Props {
  readonly credential: AffiliateCredentialPublic | undefined;
}

/** Rakuten Advertising: client id, client secret (Vault) e site id (sid). */
export function RakutenCredentialSection({ credential }: Props): JSX.Element {
  const config = credential?.config_public ?? {};
  return (
    <CredentialShell id="rakuten" title="Rakuten">
      <div className="space-y-1.5">
        <p className="text-sm font-semibold text-foreground">Rede de lojas Rakuten Advertising</p>
        {credential && (
          <p className="font-mono text-xs text-muted-foreground">
            Client {String(config["clientIdMasked"] ?? "-")} · site {String(config["siteId"] ?? "-")}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Pegue os dados em developers.rakutenadvertising.com, em Access Tokens. A loja é descoberta pelo link entre
          os seus programas aprovados; o link sai mesmo quando a loja não informa dados do produto.
        </p>
      </div>
      <CredentialHealth credential={credential} />
      <RakutenForm hasSecret={config["hasSecret"] === true} />
    </CredentialShell>
  );
}

function RakutenForm({ hasSecret }: { hasSecret: boolean }): JSX.Element {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [siteId, setSiteId] = useState("");
  const { saving, status, error, save } = useCredentialSave("rakuten", ERRORS);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const params = { p_client_id: clientId.trim(), p_client_secret: clientSecret.trim(), p_site_id: siteId.trim() };
    if (await save("upsert_rakuten_affiliate_credential", params)) {
      setClientSecret("");
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-3">
        <Field id="rakuten-client-id" label="Client ID" value={clientId} onValue={setClientId} required />
        <Field id="rakuten-client-secret" label="Client Secret" type="password" value={clientSecret} onValue={setClientSecret}
          placeholder={hasSecret ? "Deixe em branco para manter" : ""} />
        <Field id="rakuten-site-id" label="ID do site (sid)" value={siteId} onValue={setSiteId} inputMode="numeric" required />
        <div className="flex justify-end sm:col-span-3">
          <Button type="submit" variant="gold" disabled={saving}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
            Salvar e testar
          </Button>
        </div>
      </form>
      <SaveFeedback status={status} error={error} />
    </>
  );
}
